// All the useMemo derivations for app/page.tsx, bundled in one hook.
// Extracted so the home page stays under the god-file threshold; everything
// here is pure derivation off state + remote data, no local setState.

import { useMemo } from 'react';
import type { ScoredStock, StockFactors, PillarScores } from '@/lib/compute';
import { compositeScore } from '@/lib/composite-score';
import { BASKET_SIZE } from '@/lib/alpha-basket';
import type { TechnicalStock } from '@/lib/hooks/use-visualizer-data';
import { useResource } from '@/lib/use-resource';

interface AlphaBasketResp { basket?: { slots?: Array<{ current: { symbol: string; weight?: number } }> } }

export interface UseHomeDerivedInput {
  rawStocksToUse: ScoredStock[];
  vMode: 'classic' | 'blended';
  selectedSectors: Set<string>;
  selectedIndustries: Set<string>;
  minMarketCap: number;
  maxMarketCap: number;
  portfolioIsolated: boolean;
  portfolioSymbols: Set<string>;
  alphaZoneIsolated: boolean;
  highlightedFactor: keyof StockFactors | null;
  highlightedPillar: keyof PillarScores | null;
  selectedSymbol: string | null;
  technicalData: TechnicalStock[];
  technicalWeeklyData: TechnicalStock[];
  technicalMonthlyData: TechnicalStock[];
  historicalStocks: ScoredStock[] | null;
}

export function useHomeDerived(input: UseHomeDerivedInput) {
  const { rawStocksToUse, vMode, selectedSectors, selectedIndustries, minMarketCap, maxMarketCap,
    portfolioIsolated, portfolioSymbols, alphaZoneIsolated, highlightedFactor, highlightedPillar,
    selectedSymbol, technicalData, technicalWeeklyData, technicalMonthlyData, historicalStocks,
  } = input;

  // Apply the V toggle: 'blended' (default, production) reads
  // valuationScoreBlended from scores.json — the 0.75·peer-z + 0.25·universe-z
  // value already calibrated by blend-into-scores.ts. 'classic' reverts to
  // valuationScore (peer-z only). When valuationScoreBlended is missing
  // (banks, insufficient EPS history), falls back to valuationScore — no-op.
  const stocksToUse = useMemo(() => {
    if (vMode === 'classic') return rawStocksToUse;
    return rawStocksToUse.map((s) => {
      const next = { ...s };
      const vb = s.valuationScoreBlended;
      if (vb != null && Number.isFinite(vb)) next.valuationScore = vb;
      const qb = s.qualityScoreBlended;
      if (qb != null && Number.isFinite(qb)) next.qualityScore = qb;
      return next;
    });
  }, [rawStocksToUse, vMode]);

  // Alpha Zone — sized by the canonical BASKET_SIZE constant from
  // lib/alpha-basket.ts. Importing the constant (not hard-coding a number)
  // ensures the 3D visualizer, slides, /alpha-zone page, Action Monitor,
  // and basket writer all stay locked together. Steve directive 2026-05-23
  // after the 20-vs-10 drift bug ("we cant have shit like this. its too
  // rudimentary an error for a complicated system"). Both Classic and
  // Blended modes now derive count from the same source.
  const { data: alphaBasketData } = useResource<AlphaBasketResp>('/api/alpha-basket');
  const alphaZoneStocks = useMemo(() => {
    if (vMode === 'blended') {
      const ranked = stocksToUse
        .filter((s) => s.qualityScore != null && s.qualityScore >= 0 && s.valuationScore != null)
        .map((s) => ({ symbol: s.symbol, composite: compositeScore(s.qualityScore, s.valuationScore) }))
        .filter((r) => r.composite != null)
        .sort((a, b) => (b.composite as number) - (a.composite as number))
        .slice(0, BASKET_SIZE);
      return new Set(ranked.map((r) => r.symbol));
    }
    const slots = alphaBasketData?.basket?.slots;
    if (!slots) return new Set<string>();
    return new Set(
      [...slots]
        .sort((a, b) => (b.current.weight ?? 0) - (a.current.weight ?? 0))
        .slice(0, BASKET_SIZE)
        .map(s => s.current.symbol),
    );
  }, [alphaBasketData, vMode, stocksToUse]);

  const filtered = useMemo(() => {
    return stocksToUse.filter(s => {
      // Alpha Zone isolation takes precedence — show only the live basket
      // (or its blended-V equivalent), bypassing all other filters.
      if (alphaZoneIsolated && alphaZoneStocks.size > 0) {
        return alphaZoneStocks.has(s.symbol);
      }
      // Portfolio isolation: show every holding regardless of sector / cap
      // filters. NVDA's $5T cap was being clipped by the slider before this.
      if (portfolioIsolated && portfolioSymbols.size > 0) {
        return portfolioSymbols.has(s.symbol);
      }
      const isCanadian = s.symbol.endsWith('.TO') || s.symbol === 'BAM';
      if (!selectedSectors.has(s.sector) && !(selectedSectors.has('Canada') && isCanadian)) return false;
      if (selectedIndustries.size > 0 && !selectedIndustries.has(s.industry ?? '')) return false;
      if (s.marketCap < minMarketCap || s.marketCap > maxMarketCap) return false;
      return true;
    });
  }, [stocksToUse, selectedSectors, selectedIndustries, minMarketCap, maxMarketCap, portfolioIsolated, portfolioSymbols, alphaZoneIsolated, alphaZoneStocks]);

  // Top 10 stocks for the currently highlighted factor / pillar.
  const factorHighlightStocks = useMemo(() => {
    if (!stocksToUse.length) return new Set<string>();
    // Pillar highlight wins when both are set — pillar is a more recent click.
    if (highlightedPillar) {
      const withValue = stocksToUse
        .filter(s => s.pillarScores?.[highlightedPillar] != null)
        .map(s => ({ symbol: s.symbol, value: s.pillarScores[highlightedPillar] as number }));
      withValue.sort((a, b) => b.value - a.value);
      return new Set(withValue.slice(0, 10).map(s => s.symbol));
    }
    if (!highlightedFactor) return new Set<string>();
    const withValue = stocksToUse
      .filter(s => s.factors[highlightedFactor] != null)
      .map(s => ({ symbol: s.symbol, value: s.factors[highlightedFactor] as number }));
    withValue.sort((a, b) => b.value - a.value);
    return new Set(withValue.slice(0, 10).map(s => s.symbol));
  }, [highlightedFactor, highlightedPillar, stocksToUse]);

  // Top 5 by composite within the current sector/industry filter. Active when
  // the filter narrows the view; empty otherwise so the highlight disappears
  // when "all sectors" is selected.
  const sectorTopComposite = useMemo(() => {
    if (filtered.length === 0) return new Set<string>();
    if (filtered.length === stocksToUse.length) return new Set<string>();
    const ranked = filtered
      .map(s => ({ symbol: s.symbol, composite: compositeScore(s.qualityScore, s.valuationScore) }))
      .filter(r => r.composite != null)
      .sort((a, b) => (b.composite as number) - (a.composite as number))
      .slice(0, 5);
    return new Set(ranked.map(r => r.symbol));
  }, [filtered, stocksToUse.length]);

  const filteredHistorical = useMemo(() => {
    if (!historicalStocks) return null;
    if (!portfolioIsolated || portfolioSymbols.size === 0) return historicalStocks;
    return historicalStocks.filter(s => portfolioSymbols.has(s.symbol));
  }, [historicalStocks, portfolioIsolated, portfolioSymbols]);

  const selectedStock = useMemo(() => {
    if (!selectedSymbol) return null;
    return stocksToUse.find(s => s.symbol === selectedSymbol) ?? null;
  }, [selectedSymbol, stocksToUse]);

  // Combined technical packet for the selected symbol — daily/weekly/monthly
  // RSI, stochRSI, MACD bits. Returns undefined when nothing's selected or no
  // technical data is present for the symbol.
  const selectedTechnicalData = useMemo(() => {
    if (!selectedSymbol) return undefined;
    const daily = technicalData.find(t => t.symbol === selectedSymbol);
    const weekly = technicalWeeklyData.find(t => t.symbol === selectedSymbol);
    const monthly = technicalMonthlyData.find(t => t.symbol === selectedSymbol);
    const shortTerm = daily?.technicalScore ?? null;
    const longTerm = monthly?.technicalScore ?? null;
    if (shortTerm === null && longTerm === null) return undefined;
    let combined: number | null = null;
    if (shortTerm !== null && longTerm !== null) combined = (shortTerm + longTerm) / 2;
    else combined = shortTerm ?? longTerm;
    const dailyRsi = daily?.rsi ?? null;
    const weeklyRsi = weekly?.rsi ?? null;
    const monthlyRsi = monthly?.rsi ?? null;
    const dailyRsiPrev = daily?.rsiPrev ?? null;
    const weeklyRsiPrev = weekly?.rsiPrev ?? null;
    const monthlyRsiPrev = monthly?.rsiPrev ?? null;
    const rsiSum = (dailyRsi !== null && weeklyRsi !== null && monthlyRsi !== null) ? dailyRsi + weeklyRsi + monthlyRsi : null;
    return {
      shortTerm, longTerm, combined,
      dailyRsi, weeklyRsi, monthlyRsi, rsiSum,
      dailyRsiPrev, weeklyRsiPrev, monthlyRsiPrev,
      dailyStochRsi: daily?.stochRsi ?? null,
      dailyMacdHistogram: daily?.macdHistogram ?? null,
      dailyMacdLine: daily?.macdLine ?? null,
      dailySignalLine: daily?.signalLine ?? null,
      monthlyStochRsi: monthly?.stochRsi ?? null,
      monthlyMacdHistogram: monthly?.macdHistogram ?? null,
      monthlyMacdLine: monthly?.macdLine ?? null,
      monthlySignalLine: monthly?.signalLine ?? null,
    };
  }, [selectedSymbol, technicalData, technicalWeeklyData, technicalMonthlyData]);

  return {
    stocksToUse,
    alphaZoneStocks,
    filtered,
    factorHighlightStocks,
    sectorTopComposite,
    filteredHistorical,
    selectedStock,
    selectedTechnicalData,
  };
}
